import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { verifyApi } from '../api/scans.ts';
import { useSeo } from '../hooks/useSeo.ts';
// verifyApi is also used below for the certificate.pdf URL.

// Public verifier landing page. Visited when someone clicks an EAA Proof badge embedded
// on a customer's site. Anyone can load this URL — no auth, no PII.
//
// Goals:
//   1. Confirm the badge is real (so customers trust embedding it, and so visitors trust
//      what they see on customers' sites).
//   2. Show enough scan summary to be informative without dumping the full violation list
//      (which is the paying customer's data).
//   3. Convert curious visitors with a clear "scan your own site" CTA.
//
// The verifier is indexable (not noindex) — Google + AI bots crawling badged sites should
// be able to find and weight these pages.
export default function VerifyPage() {
  const { token } = useParams<{ token: string }>();
  // OG preview shows the actual badge image — that's the most compelling share asset
  // (a green/blue/amber compliance badge with a score on it beats a generic logo).
  const ogImage = token
    ? `${typeof window !== 'undefined' ? window.location.origin : 'https://eaaproof.com'}/api/verify/${encodeURIComponent(token)}/badge.svg`
    : undefined;
  useSeo({
    title: 'Accessibility audit verification',
    description: 'This website was independently audited against WCAG 2.1 AA — the technical standard referenced by the EU Accessibility Act — by EAA Proof.',
    ogImage,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['verify', token],
    queryFn: () => verifyApi.get(token!),
    enabled: Boolean(token),
    // Verifier payloads are public and stable — cache aggressively.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (isLoading) {
    return <div className="page"><p>Verifying…</p></div>;
  }

  if (error || !data) {
    return (
      <div className="page verify-page">
        <h1>Badge not found</h1>
        <p className="muted">
          This verification link doesn't match any audited scan. It may have been mistyped,
          revoked, or never issued.
        </p>
        <Link to="/" className="cta">Scan your own site</Link>
      </div>
    );
  }

  const tierClass = `tier-${data.tier.toLowerCase()}`;
  const completedAt = new Date(data.completedAt);
  const issuedAt = new Date(data.issuedAt);
  const daysSinceIssued = Math.floor((Date.now() - issuedAt.getTime()) / (1000 * 60 * 60 * 24));
  const isStale = daysSinceIssued > 180;
  const isAging = !isStale && daysSinceIssued > 90;

  return (
    <div className="page verify-page">
      <header className="verify-header">
        <h1>Verified accessibility audit</h1>
        <p className="verify-target">
          <span className="muted">Audited URL:</span>{' '}
          <a href={data.targetUrl} target="_blank" rel="noopener noreferrer">{data.targetUrl}</a>
        </p>
      </header>

      <div className={`verify-score-card ${tierClass}`}>
        <div className="verify-score-big">{data.complianceScore}<span>/ 100</span></div>
        <div className="verify-tier-label">{data.tierLabel}</div>
        <div className="verify-tier-sub">WCAG 2.1 AA — EN 301 549 aligned</div>
        {data.domainVerifiedAt && (
          <div className="verify-domain-verified" title={`Verified via ${data.domainVerificationMethod ?? 'unknown'}`}>
            ✓ Domain ownership verified
          </div>
        )}
      </div>

      {data.hasCertificatePdf && (
        <div className="verify-cert-download">
          <a
            className="cta secondary"
            href={verifyApi.certificatePdfUrl(data.token)}
            target="_blank"
            rel="noopener noreferrer"
          >
            Download audit report (PDF)
          </a>
        </div>
      )}

      {isStale && (
        <p className="verify-stale-warning">
          ⚠ This badge is over 6 months old. The site may have changed since it was issued
          on {issuedAt.toLocaleDateString()}.
        </p>
      )}
      {isAging && (
        <p className="muted small">
          Issued {issuedAt.toLocaleDateString()}. Sites change — a rescan is recommended
          every 90 days.
        </p>
      )}

      <section className="verify-counts">
        <h2>Issues found at scan time</h2>
        <div className="severity-row">
          <div className="tile critical"><strong>{data.criticalCount}</strong><span>Critical</span></div>
          <div className="tile serious"><strong>{data.seriousCount}</strong><span>Serious</span></div>
          <div className="tile moderate"><strong>{data.moderateCount}</strong><span>Moderate</span></div>
          <div className="tile minor"><strong>{data.minorCount}</strong><span>Minor</span></div>
        </div>
      </section>

      <section className="verify-meta">
        <dl>
          <dt>Scan date</dt>
          <dd>{completedAt.toLocaleDateString()}</dd>
          <dt>Badge issued</dt>
          <dd>{issuedAt.toLocaleDateString()}</dd>
          <dt>Audit tool</dt>
          <dd>EAA Proof — Playwright + axe-core, reviewed via Claude</dd>
          <dt>Compliance framework</dt>
          <dd>WCAG 2.1 AA, EN 301 549 v3.2.1, EU Directive 2019/882</dd>
        </dl>
      </section>

      <section className="verify-cta">
        <h2>Scan your own site</h2>
        <p>
          Get a WCAG 2.1 audit and a downloadable accessibility statement in under 60 seconds.
        </p>
        <Link to="/" className="cta">Start a free scan</Link>
      </section>

      <footer className="verify-footer muted small">
        This badge is issued and verified by EAA Proof.
        <br />
        Token: <code>{data.token}</code>
      </footer>
    </div>
  );
}
